import {
  getColorByPath,
  getCssSize,
  CSSDimension,
  Integer,
  Show,
  Text,
  ThemeColorPath,
} from 'platform/foundation';
import {cn} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {CSSProperties, ReactNode} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {Context} from './context';
import {TableVariant} from './types/TableVariant';

export type StickyHeader = {
  /**
   * Distance the header keeps from the top of the scroll container, for layouts where something
   * else (a sticky page header) already occupies it.
   */
  offset?: CSSDimension | Integer;
  /**
   * Background painted behind the header so the rows do not show through it. Set it to the surface
   * the table sits on; defaults to white.
   */
  background?: ThemeColorPath;
};

export type Column = {
  element?: ReactNode;
  width?: CSSDimension | Integer;
  colspan?: number;
};

export interface TableProps extends TestIdProps {
  tableLayout?: CSSProperties['tableLayout'];
  variant?: TableVariant;
  children?: ReactNode;
  columns?: Column[];
  /**
   * Keeps the header row pinned to the top of the nearest scroll container while the body scrolls.
   * Pass an object to place it below a sticky page header or to match a non-white surface.
   */
  stickyHeader?: boolean | StickyHeader;
  role?: 'table' | 'grid';
  ariaRowCount?: number;
  ariaColCount?: number;
}

export function Table(props: TableProps) {
  const theme = useComponentsTheme();
  const stickyHeader = match<TableProps['stickyHeader'], StickyHeader | undefined>(
    props.stickyHeader
  )
    .with(Pattern.nullish, false, () => undefined)
    .with(true, () => ({}))
    .otherwise((config) => config);

  return (
    <Context.Provider value={{variant: props.variant ?? 'bordered'}}>
      <table
        data-testid={suffixTestId('table', props)}
        role={props.role}
        aria-rowcount={props.ariaRowCount}
        aria-colcount={props.ariaColCount}
        className="w-full"
        style={{tableLayout: props.tableLayout}}
        cellPadding="0"
        cellSpacing="0"
      >
        <Show when={props.columns}>
          <thead className="border-b border-b-[var(--color-general-separator)]">
            <tr>
              {props.columns?.map((column, index) => {
                const headerCellStyle: CSSProperties = {
                  width: getCssSize(column.width) ?? 'auto',
                };

                if (stickyHeader) {
                  headerCellStyle.top = getCssSize(stickyHeader.offset) ?? 0;
                  headerCellStyle.backgroundColor = getColorByPath(
                    stickyHeader.background ?? 'general.white',
                    theme
                  );
                  headerCellStyle.boxShadow = `inset 0 -1px 0 ${theme.colors.general.separator}`;
                }

                return (
                  <th
                    colSpan={column.colspan}
                    key={`${column.width ?? ''}-${index}`} // eslint-disable-line react/no-array-index-key -- Static column definitions have no stable identifier.
                    className={cn(stickyHeader && 'sticky z-[1] print:static')}
                    style={headerCellStyle}
                  >
                    <Text>{column.element}</Text>
                  </th>
                );
              })}
            </tr>
          </thead>
        </Show>
        <tbody>
          <Show when={props.children}>{props.children}</Show>
        </tbody>
      </table>
    </Context.Provider>
  );
}
